import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Upload, Video } from 'lucide-react';
import api from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    category: 'gemstone',
    gem_type: 'emerald',
    shape: '',
    carat_weight: '',
    dimensions: '',
    color_description: '',
    clarity_description: '',
    origin: '',
    treatment: 'Untreated',
    certification_lab: '',
    certification_id: '',
    description_long: '',
    price: '',
    price_on_request: false,
    stock_status: 'in_stock',
    is_featured: false,
  });

  const [mainImage, setMainImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [videoUrls, setVideoUrls] = useState(['']);
  const [videoFiles, setVideoFiles] = useState([]);
  
  // Preview states
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [galleryImagePreviews, setGalleryImagePreviews] = useState([]);
  const [videoFilePreviews, setVideoFilePreviews] = useState([]);
  
  // Track which existing images/videos to keep when editing
  const [existingGalleryToKeep, setExistingGalleryToKeep] = useState([]);
  const [existingVideosToKeep, setExistingVideosToKeep] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products?sort_by=created_at&sort_order=desc');
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploadingImages(true);
    
    try {
      const payload = {
        ...formData,
        carat_weight: parseFloat(formData.carat_weight),
        price: formData.price_on_request ? null : parseFloat(formData.price) || null,
      };

      let productId;
      
      if (editingProduct) {
        await api.put(`/admin/products/${editingProduct.id}`, payload);
        productId = editingProduct.id;
        
        // Update kept gallery images if user deleted any
        if (existingGalleryToKeep.length !== editingProduct.gallery_image_urls?.length) {
          await api.put(`/admin/products/${productId}/gallery-kept`, {
            kept_images: existingGalleryToKeep
          });
        }
        
        // Update kept videos if user deleted any
        if (existingVideosToKeep.length !== editingProduct.video_urls?.length) {
          await api.put(`/admin/products/${productId}/videos-kept`, {
            kept_videos: existingVideosToKeep
          });
        }
        
        toast.success('Product updated');
      } else {
        const response = await api.post('/admin/products', payload);
        productId = response.data.id;
        toast.success('Product created');
      }

      // Upload images if any
      if (mainImage || galleryImages.length > 0) {
        console.log('Uploading images...', { mainImage, galleryCount: galleryImages.length });
        await uploadImages(productId);
      }

      // Collect all video URLs - both uploaded files and external URLs
      let allVideoUrls = [];

      // Upload video files if any
      if (videoFiles.length > 0) {
        console.log('Uploading video files...', { count: videoFiles.length });
        const uploadedVideos = await uploadVideoFiles(productId);
        if (uploadedVideos && uploadedVideos.length > 0) {
          allVideoUrls = [...allVideoUrls, ...uploadedVideos];
        }
      }

      // Add external video URLs (YouTube, Vimeo, etc.)
      const validExternalUrls = videoUrls.filter(url => url.trim() && (url.includes('youtube') || url.includes('vimeo') || url.startsWith('http')));
      if (validExternalUrls.length > 0) {
        allVideoUrls = [...allVideoUrls, ...validExternalUrls];
      }

      // If editing and keeping existing videos, merge them
      if (editingProduct && existingVideosToKeep.length > 0) {
        // Filter out duplicates
        const existingUrls = existingVideosToKeep.filter(url => !allVideoUrls.includes(url));
        allVideoUrls = [...existingUrls, ...allVideoUrls];
      }

      // Save all video URLs together
      if (allVideoUrls.length > 0 || (editingProduct && existingVideosToKeep.length === 0)) {
        console.log('Saving all video URLs...', { urls: allVideoUrls });
        await api.put(`/admin/products/${productId}/videos`, {
          video_urls: allVideoUrls
        });
      }

      toast.success('All media uploaded successfully!');
      await fetchProducts();
      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setUploadingImages(false);
    }
  };

  const uploadImages = async (productId) => {
    setUploadingImages(true);
    try {
      const formData = new FormData();
      
      if (mainImage) {
        formData.append('main_image', mainImage);
      }
      
      if (galleryImages.length > 0) {
        galleryImages.forEach(img => {
          formData.append('gallery_images', img);
        });
      }

      console.log('Uploading:', { main: !!mainImage, gallery: galleryImages.length });

      const response = await api.post(`/admin/products/${productId}/images`, formData, {
        headers: { 'Content-Type': undefined },
        timeout: 60000 // 60 seconds for large files
      });
      
      console.log('Upload response:', response.data);
      
      // Validate upload success
      if (mainImage && !response.data.main_image_url) {
        throw new Error('Main image upload failed');
      }
      if (galleryImages.length > 0 && (!response.data.gallery_image_urls || response.data.gallery_image_urls.length < galleryImages.length)) {
        console.warn('Not all gallery images uploaded successfully');
      }
      
      toast.success(`✓ Images uploaded! Gallery: ${response.data.gallery_image_urls?.length || 0}`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload images');
      throw error;
    } finally {
      setUploadingImages(false);
    }
  };

  const uploadVideoFiles = async (productId) => {
    if (videoFiles.length === 0) return [];
    
    try {
      toast.info('Uploading and compressing video... This may take a few minutes for large files.');
      
      const formData = new FormData();
      videoFiles.forEach(video => {
        formData.append('video_files', video);
      });

      const response = await api.post(`/admin/products/${productId}/video-files`, formData, {
        headers: { 'Content-Type': undefined },
        timeout: 300000 // 5 minutes for large video files (compression takes time)
      });
      
      toast.success('Videos uploaded successfully');
      return response.data.video_urls || [];
    } catch (error) {
      console.error('Video upload error:', error);
      toast.error('Failed to upload videos');
      return [];
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category || 'gemstone',
      gem_type: product.gem_type,
      shape: product.shape || '',
      carat_weight: product.carat_weight.toString(),
      dimensions: product.dimensions || '',
      color_description: product.color_description,
      clarity_description: product.clarity_description,
      origin: product.origin,
      treatment: product.treatment,
      certification_lab: product.certification_lab || '',
      certification_id: product.certification_id || '',
      description_long: product.description_long,
      price: product.price?.toString() || '',
      price_on_request: product.price_on_request,
      stock_status: product.stock_status,
      is_featured: product.is_featured,
    });
    
    // Initialize existing images/videos to keep
    setExistingGalleryToKeep(product.gallery_image_urls || []);
    setExistingVideosToKeep(product.video_urls || []);
    
    setVideoUrls(product.video_urls?.length > 0 ? product.video_urls : ['']);
    
    // Reset upload states
    setMainImage(null);
    setMainImagePreview(null);
    setGalleryImages([]);
    setGalleryImagePreviews([]);
    setVideoFiles([]);
    setVideoFilePreviews([]);
    
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'gemstone',
      gem_type: 'emerald',
      shape: '',
      carat_weight: '',
      dimensions: '',
      color_description: '',
      clarity_description: '',
      origin: '',
      treatment: 'Untreated',
      certification_lab: '',
      certification_id: '',
      description_long: '',
      price: '',
      price_on_request: false,
      stock_status: 'in_stock',
      is_featured: false,
    });
    setEditingProduct(null);
    setMainImage(null);
    setGalleryImages([]);
    setVideoUrls(['']);
    setVideoFiles([]);
    
    // Clear previews
    setMainImagePreview(null);
    setGalleryImagePreviews([]);
    setVideoFilePreviews([]);
  };

  const addVideoUrlField = () => {
    setVideoUrls([...videoUrls, '']);
  };

  const updateVideoUrl = (index, value) => {
    const updated = [...videoUrls];
    updated[index] = value;
    setVideoUrls(updated);
  };

  const removeVideoUrl = (index) => {
    setVideoUrls(videoUrls.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-serif text-4xl mb-2">Product Management</h1>
            <p className="text-gray-600">{products.length} products total</p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} className="bg-black text-white hover:bg-gray-900" data-testid="add-product-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Product Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., 5.12 ct Colombian Emerald"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select name="category" value={formData.category} onValueChange={(value) => handleInputChange({ target: { name: 'category', value } })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        <SelectItem value="gemstone">Loose Gemstone</SelectItem>
                        <SelectItem value="jewellery">Jewellery</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="gem_type">Gemstone Type *</Label>
                    <Select name="gem_type" value={formData.gem_type} onValueChange={(value) => handleInputChange({ target: { name: 'gem_type', value } })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        <SelectItem value="emerald">Emerald</SelectItem>
                        <SelectItem value="ruby">Ruby</SelectItem>
                        <SelectItem value="sapphire">Sapphire</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="carat_weight">Carat Weight *</Label>
                    <Input
                      id="carat_weight"
                      name="carat_weight"
                      type="number"
                      step="0.01"
                      value={formData.carat_weight}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="origin">Origin *</Label>
                    <Input
                      id="origin"
                      name="origin"
                      value={formData.origin}
                      onChange={handleInputChange}
                      placeholder="e.g., Colombia, Myanmar"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="treatment">Treatment *</Label>
                    <Select name="treatment" value={formData.treatment} onValueChange={(value) => handleInputChange({ target: { name: 'treatment', value } })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select treatment" />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        <SelectItem value="Untreated">Untreated</SelectItem>
                        <SelectItem value="Unheated">Unheated</SelectItem>
                        <SelectItem value="Heated">Heated</SelectItem>
                        <SelectItem value="Minor oil">Minor Oil</SelectItem>
                        <SelectItem value="Moderate oil">Moderate Oil</SelectItem>
                        <SelectItem value="Unknown">Unknown</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="color_description">Color Description *</Label>
                  <Input
                    id="color_description"
                    name="color_description"
                    value={formData.color_description}
                    onChange={handleInputChange}
                    placeholder="e.g., Vivid Green, Pigeon Blood Red"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="clarity_description">Clarity Description *</Label>
                  <Input
                    id="clarity_description"
                    name="clarity_description"
                    value={formData.clarity_description}
                    onChange={handleInputChange}
                    placeholder="e.g., VVS - Eye clean"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="description_long">Full Description * (Searchable)</Label>
                  <Textarea
                    id="description_long"
                    name="description_long"
                    value={formData.description_long}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Detailed description of the gemstone..."
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="dimensions">Dimensions</Label>
                    <Input
                      id="dimensions"
                      name="dimensions"
                      value={formData.dimensions}
                      onChange={handleInputChange}
                      placeholder="e.g., 11.2 x 9.8 x 7.1 mm"
                    />
                  </div>

                  <div>
                    <Label htmlFor="shape">Shape</Label>
                    <Input
                      id="shape"
                      name="shape"
                      value={formData.shape}
                      onChange={handleInputChange}
                      placeholder="e.g., Oval, Cushion"
                    />
                  </div>

                  <div>
                    <Label htmlFor="certification_lab">Certification Lab</Label>
                    <Input
                      id="certification_lab"
                      name="certification_lab"
                      value={formData.certification_lab}
                      onChange={handleInputChange}
                      placeholder="e.g., GIA, GRS"
                    />
                  </div>

                  <div>
                    <Label htmlFor="certification_id">Certificate ID</Label>
                    <Input
                      id="certification_id"
                      name="certification_id"
                      value={formData.certification_id}
                      onChange={handleInputChange}
                      placeholder="Certificate number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>
                      <input
                        type="checkbox"
                        name="price_on_request"
                        checked={formData.price_on_request}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      Price on Request
                    </Label>
                  </div>

                  {!formData.price_on_request && (
                    <div>
                      <Label htmlFor="price">Price (USD)</Label>
                      <Input
                        id="price"
                        name="price"
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={handleInputChange}
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="stock_status">Stock Status</Label>
                    <Select name="stock_status" value={formData.stock_status} onValueChange={(value) => handleInputChange({ target: { name: 'stock_status', value } })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        <SelectItem value="in_stock">In Stock</SelectItem>
                        <SelectItem value="reserved">Reserved</SelectItem>
                        <SelectItem value="sold">Sold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>
                      <input
                        type="checkbox"
                        name="is_featured"
                        checked={formData.is_featured}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      Featured Product
                    </Label>
                  </div>
                </div>

                {/* Image Upload */}
                <div className="border-t pt-6">
                  <h3 className="font-medium mb-4">Images & Videos</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="main_image">Main Image</Label>
                      {!mainImagePreview && !editingProduct?.main_image_url && (
                        <Input
                          id="main_image"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            setMainImage(file);
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => setMainImagePreview(reader.result);
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      )}
                      {mainImagePreview && (
                        <div className="mt-2 relative inline-block">
                          <img src={mainImagePreview} alt="Main preview" className="w-full max-w-md h-64 object-cover border" />
                          <button
                            type="button"
                            onClick={() => {
                              setMainImage(null);
                              setMainImagePreview(null);
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 font-bold"
                          >
                            ×
                          </button>
                          <p className="text-xs text-green-600 mt-1">✓ Ready to upload</p>
                        </div>
                      )}
                      {editingProduct?.main_image_url && !mainImagePreview && (
                        <div className="mt-2 relative inline-block">
                          <p className="text-xs text-gray-600 mb-1">Current main image:</p>
                          <img 
                            src={editingProduct.main_image_url.startsWith('http') ? editingProduct.main_image_url : `${process.env.REACT_APP_BACKEND_URL}${editingProduct.main_image_url}`} 
                            alt="Current main" 
                            className="w-full max-w-md h-64 object-cover border" 
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1689777239202-a7a34baae6d7?w=400&q=80';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              document.getElementById('main_image_replace').click();
                            }}
                            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                          >
                            Replace Image
                          </button>
                          <Input
                            id="main_image_replace"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              setMainImage(file);
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => setMainImagePreview(reader.result);
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="gallery_images">Gallery Images (Multiple)</Label>
                      <Input
                        id="gallery_images"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          setGalleryImages([...galleryImages, ...files]);
                          
                          // Generate previews
                          const newPreviews = [];
                          files.forEach(file => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              newPreviews.push(reader.result);
                              if (newPreviews.length === files.length) {
                                setGalleryImagePreviews([...galleryImagePreviews, ...newPreviews]);
                              }
                            };
                            reader.readAsDataURL(file);
                          });
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">Select multiple images - you can add more later</p>
                      {galleryImagePreviews.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-green-600 mb-2">✓ {galleryImagePreviews.length} image(s) ready to upload</p>
                          <div className="grid grid-cols-3 gap-2">
                            {galleryImagePreviews.map((preview, idx) => (
                              <div key={idx} className="relative">
                                <img src={preview} alt={`Gallery preview ${idx}`} className="w-full h-32 object-cover border" />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newPreviews = galleryImagePreviews.filter((_, i) => i !== idx);
                                    const newFiles = galleryImages.filter((_, i) => i !== idx);
                                    setGalleryImagePreviews(newPreviews);
                                    setGalleryImages(newFiles);
                                  }}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 text-xs font-bold"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {editingProduct && existingGalleryToKeep.length > 0 && (
                        <div className="mt-2 mb-4">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Current Gallery ({existingGalleryToKeep.length} images):</p>
                          <div className="grid grid-cols-3 gap-2">
                            {existingGalleryToKeep.map((url, idx) => {
                              const imageUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                              return (
                                <div key={idx} className="relative group">
                                  <img 
                                    src={imageUrl} 
                                    alt={`Gallery ${idx + 1}`} 
                                    className="w-full h-32 object-cover border-2 border-gray-200 rounded"
                                    onError={(e) => {
                                      console.error(`Failed to load image: ${imageUrl}`);
                                      e.target.src = 'https://via.placeholder.com/200x200/f3f4f6/9ca3af?text=Load+Error';
                                      e.target.parentElement.classList.add('border-red-300');
                                    }}
                                    onLoad={(e) => {
                                      // Image loaded successfully
                                      e.target.parentElement.classList.remove('border-red-300');
                                      e.target.parentElement.classList.add('border-green-300');
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newGallery = existingGalleryToKeep.filter((_, i) => i !== idx);
                                      setExistingGalleryToKeep(newGallery);
                                      toast.success('Image removed (save to confirm)');
                                    }}
                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 text-sm font-bold shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Delete this image"
                                  >
                                    ×
                                  </button>
                                  <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                    {idx + 1}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-xs text-blue-600 mt-2">💡 Hover over image to see delete button • Upload new images above to add more</p>
                        </div>
                      )}
                      {editingProduct?.gallery_image_urls?.length > 0 && galleryImagePreviews.length === 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-600 mb-1">Current gallery images (add new ones above):</p>
                          <div className="grid grid-cols-3 gap-2">
                            {editingProduct.gallery_image_urls.map((url, idx) => (
                              <img 
                                key={idx} 
                                src={url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} 
                                alt={`Current gallery ${idx}`} 
                                className="w-full h-32 object-cover border"
                                onError={(e) => {
                                  e.target.src = 'https://images.unsplash.com/photo-1689777239202-a7a34baae6d7?w=200&q=80';
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="video_files">Upload Video Files (MP4, MOV, etc.)</Label>
                      <Input
                        id="video_files"
                        type="file"
                        accept="video/*"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          setVideoFiles([...videoFiles, ...files]);
                          
                          // Generate video previews (video elements)
                          const newPreviews = [];
                          files.forEach(file => {
                            const url = URL.createObjectURL(file);
                            newPreviews.push({ name: file.name, url });
                          });
                          setVideoFilePreviews([...videoFilePreviews, ...newPreviews]);
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">Upload videos from your device - MP4 works best</p>
                      {videoFilePreviews.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-green-600 mb-2">✓ {videoFilePreviews.length} video(s) ready to upload</p>
                          <div className="space-y-2">
                            {videoFilePreviews.map((preview, idx) => (
                              <div key={idx} className="border p-2 relative">
                                <p className="text-xs mb-1 font-medium">{preview.name}</p>
                                <video 
                                  src={preview.url} 
                                  controls 
                                  preload="metadata" 
                                  className="w-full max-w-md h-48 bg-black"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    URL.revokeObjectURL(preview.url);
                                    const newPreviews = videoFilePreviews.filter((_, i) => i !== idx);
                                    const newFiles = videoFiles.filter((_, i) => i !== idx);
                                    setVideoFilePreviews(newPreviews);
                                    setVideoFiles(newFiles);
                                  }}
                                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 font-bold"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {editingProduct && existingVideosToKeep.length > 0 && (
                        <div className="mt-2 mb-4">
                          <p className="text-xs text-gray-600 mb-2">Current videos (click X to remove):</p>
                          <div className="space-y-2">
                            {existingVideosToKeep.map((url, idx) => {
                              const videoUrl = url.startsWith('http') || url.startsWith('/api/uploads/') 
                                ? url 
                                : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                              
                              return (
                                <div key={idx} className="relative border p-2 rounded">
                                  <video 
                                    src={`${videoUrl}#t=0.1`}
                                    controls 
                                    preload="metadata" 
                                    className="w-full max-w-md h-48 bg-black rounded"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newVideos = existingVideosToKeep.filter((_, i) => i !== idx);
                                      setExistingVideosToKeep(newVideos);
                                    }}
                                    className="absolute top-3 right-3 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 text-sm font-bold shadow-lg"
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Add new videos above to append to collection</p>
                        </div>
                      )}
                      {editingProduct?.video_urls?.length > 0 && videoFilePreviews.length === 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-600 mb-1">Current videos (add new ones above):</p>
                          <div className="space-y-2">
                            {editingProduct.video_urls.map((url, idx) => {
                              const videoUrl = url.startsWith('http') || url.startsWith('/api/uploads/') 
                                ? url 
                                : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                              
                              return (
                                <div key={idx} className="border p-2">
                                  <video 
                                    src={`${videoUrl}#t=0.1`}
                                    controls 
                                    preload="metadata" 
                                    className="w-full max-w-md h-48 bg-black"
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label>Or Video URLs (YouTube, Vimeo, etc.)</Label>
                      {videoUrls.map((url, index) => (
                        <div key={index} className="flex gap-2 mt-2">
                          <Input
                            value={url}
                            onChange={(e) => updateVideoUrl(index, e.target.value)}
                            placeholder="https://youtube.com/..."
                          />
                          {videoUrls.length > 1 && (
                            <Button type="button" variant="outline" size="sm" onClick={() => removeVideoUrl(index)}>
                              Remove
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={addVideoUrlField} className="mt-2">
                        <Video className="w-4 h-4 mr-2" />
                        Add Video URL
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button type="submit" className="flex-1 bg-black text-white hover:bg-gray-900" disabled={uploadingImages}>
                    {uploadingImages ? 'Uploading...' : editingProduct ? 'Update Product' : 'Create Product'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Products List */}
        {loading ? (
          <div className="text-center py-20">Loading products...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map(product => (
              <div key={product.id} className="border border-gray-200 group" data-testid={`admin-product-${product.id}`}>
                <div className="aspect-square bg-gray-50">
                  <img
                    src={product.main_image_url || 'https://images.unsplash.com/photo-1689777239202-a7a34baae6d7?w=400&q=80'}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4 space-y-2">
                  <h3 className="font-medium text-sm line-clamp-2">{product.name}</h3>
                  <div className="text-xs text-gray-600">
                    <div>{product.carat_weight} ct • {product.gem_type}</div>
                    <div>{product.origin}</div>
                  </div>
                  <p className="text-sm font-semibold">
                    {product.price_on_request ? 'POA' : formatPrice(product.price, product.currency)}
                  </p>
                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(product)}
                      className="flex-1"
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(product.id)}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`delete-product-${product.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;
